import React from "react";
import { Link } from "react-router-dom";
import { Stethoscope, FileText, Sparkles, Share2, ArrowRight, LogIn, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBwYXRpZW50JTIwY29uc3VsdGF0aW9ufGVufDB8fHx8MTc4MTgyNDk5OHww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg-canvas)" }}>
      <header className="px-6 md:px-12 py-6 max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" data-testid="landing-brand" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-semibold text-lg">MedNarrative</span>
        </Link>
        <div className="auth-cta-cluster flex items-center gap-2">
          <Link
            data-testid="landing-login"
            to="/login"
            className="auth-cta auth-cta-ghost inline-flex items-center gap-1.5 text-sm font-medium pl-4 pr-4 py-2 rounded-full border"
            style={{ borderColor: "var(--color-border)", color: "var(--color-text-primary)" }}
          >
            <LogIn className="w-3.5 h-3.5" aria-hidden />
            Sign in
          </Link>
          <Link
            data-testid="landing-register"
            to="/register"
            className="auth-cta auth-cta-primary inline-flex items-center gap-1.5 text-sm font-semibold pl-4 pr-3.5 py-2 rounded-full text-white"
            style={{ background: "var(--color-primary)" }}
          >
            Create account
            <ArrowRight className="w-3.5 h-3.5" aria-hidden />
          </Link>
        </div>
      </header>

      <section className="px-6 md:px-12 max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center pt-8 md:pt-16 pb-20">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border" style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}>
            <Sparkles className="w-3 h-3" /> AI imaging reports
          </div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" style={{ color: "var(--color-text-primary)" }}>
            Radiology reports,<br />
            <span style={{ color: "var(--color-primary)" }}>written like a human</span>.
          </h1>
          <p className="mt-6 text-base md:text-lg leading-relaxed max-w-lg" style={{ color: "var(--color-text-secondary)" }}>
            Upload any X-ray, MRI or CT report. We turn dense radiology jargon into two clear narratives —
            a precise clinical summary for doctors, and a warm, plain-English note for patients.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              data-testid="hero-cta-register"
              to="/register"
              className="auth-cta auth-cta-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium"
              style={{ background: "var(--color-primary)" }}
            >
              Try it free
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              data-testid="hero-cta-login"
              to="/login"
              className="auth-cta auth-cta-ghost inline-flex items-center gap-2 px-6 py-3 rounded-full border font-medium"
              style={{ borderColor: "var(--color-border)", color: "var(--color-text-primary)" }}
            >
              <LogIn className="w-4 h-4" />
              I already have an account
            </Link>
          </div>
          <div className="mt-5 flex items-center gap-2 text-xs" style={{ color: "var(--color-text-secondary)" }}>
            <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--color-success)" }} />
            <span>HIPAA-aware · No credit card · Free for the first 50 reports</span>
          </div>
        </div>
        <div className="relative">
          <img src={HERO_IMG} alt="Clinician with laptop" className="rounded-2xl w-full object-cover h-[420px] shadow-sm" />
          <div className="absolute -bottom-6 -left-6 bg-white border rounded-xl p-5 max-w-[260px] shadow-sm fade-up" style={{ borderColor: "var(--color-border)" }}>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Patient view</div>
            <p className="font-serif-report mt-2 text-[15px] leading-relaxed" style={{ color: "var(--color-text-primary)" }}>
              &ldquo;Good news — your scan looks healthy. There&apos;s a small spot we want to keep an eye on, but nothing to worry about today.&rdquo;
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-24 grid md:grid-cols-3 gap-6">
        {[
          { icon: FileText, title: "Drop a report in", body: "PDF, JPG or PNG. We extract every line verbatim, no template required." },
          { icon: Sparkles, title: "Two narratives, one click", body: "Claude Sonnet 4.6 writes a precise doctor summary and a warm patient letter." },
          { icon: Share2, title: "Share, print, archive", body: "Generate a read-only link or print to PDF. History grouped by patient." },
        ].map(({ icon: Icon, title, body }) => (
          <div key={title} className="bg-white border rounded-lg p-6" style={{ borderColor: "var(--color-border)" }}>
            <Icon className="w-6 h-6" style={{ color: "var(--color-primary)" }} />
            <h3 className="font-display font-semibold text-xl mt-4">{title}</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--color-text-secondary)" }}>{body}</p>
          </div>
        ))}
      </section>

      <footer className="border-t py-6 text-center text-xs" style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}>
        MedNarrative · AI-assisted reporting · Not a substitute for clinical judgement.
      </footer>
    </div>
  );
}
